import React from 'react';
import { Table, Space, Tooltip, Button, Typography, Badge, Tag } from 'antd';
import {
    EyeOutlined, LinkOutlined, CloudDownloadOutlined,
    FireOutlined, UserOutlined, ClockCircleOutlined,
    EyeFilled, MessageOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CommunityPostItem } from '../../../services/dashboardService';
import '../CommunityPosts.css'; // Ensure CSS is available

const { Text } = Typography;

interface PostListTableProps {
    posts: CommunityPostItem[];
    loading: boolean;
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number, pageSize: number) => void;
    onViewDetail: (post: CommunityPostItem) => void;
    onScrapeDetail: (id: number) => void;
}

export const PostListTable: React.FC<PostListTableProps> = ({
    posts, loading, page, pageSize, total,
    onPageChange, onViewDetail, onScrapeDetail
}) => {
    const getStatusTag = (status: string) => {
        switch (status) {
            case 'scraped': return <Tag color="success">已抓取</Tag>;
            case 'pending': return <Tag color="warning">待抓取</Tag>;
            case 'failed': return <Tag color="error">失败</Tag>;
            default: return <Tag>{status}</Tag>;
        }
    };

    const columns: ColumnsType<CommunityPostItem> = [
        {
            title: '排名',
            dataIndex: 'hot_rank',
            key: 'hot_rank',
            width: 70,
            render: (rank, record) => (
                record.is_hot && rank ? (
                    <Badge count={rank} style={{ backgroundColor: rank <= 3 ? '#f5222d' : '#1890ff' }} />
                ) : '-'
            ),
        },
        {
            title: '标题',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (title, record) => (
                <div className="post-title-cell">
                    <a href={record.url} target="_blank" rel="noopener noreferrer" className="post-title-link">
                        {title}
                    </a>
                    {record.is_hot && <FireOutlined style={{ color: '#f5222d', marginLeft: 8 }} />}
                </div>
            ),
        },
        {
            title: '作者',
            dataIndex: 'author',
            key: 'author',
            width: 120,
            ellipsis: true,
            render: (author) => (
                <Text type="secondary"><UserOutlined style={{ marginRight: 4 }} />{author}</Text>
            ),
        },
        {
            title: '时间',
            dataIndex: 'time',
            key: 'time',
            width: 140,
            render: (time) => (
                <Text type="secondary" style={{ fontSize: 12 }}><ClockCircleOutlined style={{ marginRight: 4 }} />{time}</Text>
            ),
        },
        {
            title: '统计',
            key: 'stats',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="浏览数">
                        <Text type="secondary"><EyeFilled style={{ marginRight: 4 }} />{record.view_count}</Text>
                    </Tooltip>
                    <Tooltip title="回复数">
                        <Text type="secondary"><MessageOutlined style={{ marginRight: 4 }} />{record.reply_count}</Text>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: '状态',
            dataIndex: 'scrape_status',
            key: 'scrape_status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="查看详情">
                        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onViewDetail(record)} />
                    </Tooltip>
                    <Tooltip title="访问原文">
                        <Button type="text" size="small" icon={<LinkOutlined />} href={record.url} target="_blank" />
                    </Tooltip>
                    {record.scrape_status !== 'scraped' && (
                        <Tooltip title="抓取内容">
                            <Button type="text" size="small" icon={<CloudDownloadOutlined />} onClick={() => onScrapeDetail(record.id)} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={posts}
            rowKey="id"
            loading={loading}
            pagination={{
                current: page,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: onPageChange,
            }}
        />
    );
};
